import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {Braces, FilePlus2, History, Lock, RefreshCw, RotateCcw, Save, Table2, Trash2, Undo2, WifiOff} from "lucide-react";
import {mapApiError} from "../../../shared/api/errors";
import {AdminTable} from "../../../shared/components/AdminTable";
import {DataState} from "../../../shared/components/DataState";
import {StatusBadge} from "../../../shared/components/StatusBadge";
import {appTextsKeys, loadAppTextSection, previewAppTextSection, saveAppTextSection} from "../appTextsApi";
import {
    KIND_LABELS,
    OFFLINE_COPY,
    OFFLINE_TITLE,
    appTextsErrorMessage,
    changedKeys,
    clearStoredDraft,
    deltaFromValues,
    diffOverride,
    draftFromStored,
    draftStorageKey,
    draftValues,
    editorModeForKind,
    formatShortDateTime,
    isJsonDraftDirty,
    matchesSearch,
    onboardingBaseDraft,
    otherFieldErrors,
    pageDraftErrors,
    pageDraftFromJson,
    pageRows,
    pageValuesError,
    parseJsonDraft,
    prettyJson,
    queryWaitState,
    readStoredDraft,
    rebaseDraft,
    rebaseResult,
    rowFieldErrors,
    saveResultMessage,
    savedDraftBase,
    stableStringify,
    startDraft,
    storableDraft,
    updatedLabel,
    warningsByKey,
    writeStoredDraft,
} from "../appTextsContracts";
import {AppTextsJsonEditor} from "./AppTextsJsonEditor";
import {AppTextsTable, WarningList} from "./AppTextsTable";
import {ConfirmDialog} from "./ConfirmDialog";
import {useTranslation, i18n} from "../../../shared/i18n";


const PREVIEW_DEBOUNCE_MS = 400;
const DRAFT_PERSIST_MS = 500;
const CONFLICT_MESSAGE = i18n.t("sections.content.p64");
// Statuses at which the server rejected the draft itself; saving it would fail the same way.
const PREVIEW_REJECTED_STATUSES = new Set([400, 422]);
// Result lines that the next edit may clear; errors and conflicts stay until handled.
const TRANSIENT_RESULTS = new Set(["success", "info"]);

const useDebouncedValue = (value, delay) => {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const timer = window.setTimeout(() => setDebounced(value), delay);
        return () => window.clearTimeout(timer);
    }, [delay, value]);
    return debounced;
};

const mergeErrors = (...sources) => sources.reduce((merged, source) => {
    Object.entries(source || {}).forEach(([key, messages]) => {
        merged[key] = [...(merged[key] || []), ...messages];
    });
    return merged;
}, {});

const EditorShell = ({children}) => (
    <section aria-label={i18n.t("sections.content.p65")} className="content-editor content-texts-editor">{children}</section>
);

export const AppTextsEditor = ({countryId, locale, section, canManage, search = "", onDirtyChange}) => {
    const {t} = useTranslation();
    const queryClient = useQueryClient();
    const sectionKey = appTextsKeys.section(countryId, locale, section);
    const storageKey = draftStorageKey({countryId, locale, section});
    const sectionQuery = useQuery({
        queryKey: sectionKey,
        queryFn: () => loadAppTextSection({countryId, locale, section}),
    });
    const view = sectionQuery.data;
    const kind = view?.section?.kind;
    const mode = editorModeForKind(kind);
    const rows = useMemo(() => (Array.isArray(view?.rows) ? view.rows : []), [view]);
    const canEdit = Boolean(view) && Boolean(canManage) && view.can_edit === true && !view.base_missing;

    // null = the draft follows the saved section; otherwise see startDraft().
    const [draftState, setDraftState] = useState(null);
    const [conflictKeys, setConflictKeys] = useState([]);
    const [reloading, setReloading] = useState(false);
    const [pageView, setPageView] = useState("table");
    const [pageStarted, setPageStarted] = useState(false);
    const [result, setResult] = useState(null);
    const [saveFieldErrors, setSaveFieldErrors] = useState(null);
    const [resetOpen, setResetOpen] = useState(false);
    const [storedDraft, setStoredDraft] = useState(() => readStoredDraft(storageKey));
    const draftRef = useRef(draftState);
    draftRef.current = draftState;

    const savedBase = useMemo(() => savedDraftBase(view), [view]);
    const {savedValues, savedDocument: savedJsonDocument} = savedBase;
    const savedDocument = useMemo(() => (mode === "json" ? savedJsonDocument : {}), [mode, savedJsonDocument]);
    const savedJsonText = Object.keys(savedDocument).length ? prettyJson(savedDocument) : "";
    const values = useMemo(() => draftValues(savedValues, draftState), [draftState, savedValues]);
    const jsonActive = mode === "json" || (mode === "page" && pageView === "json");
    const text = draftState?.text ?? (mode === "json" ? savedJsonText : prettyJson(diffOverride({rows, draft: values, mode: "page"})));

    const draftInfo = useMemo(() => {
        if (!view) return {dirty: false, payload: null, parseError: null, changed: []};
        if (jsonActive) {
            const parsed = parseJsonDraft(text);
            const parseError = parsed.error || (mode === "page" ? pageValuesError(parsed.value) : null);
            return {
                dirty: parseError ? true : stableStringify(parsed.value) !== stableStringify(savedJsonDocument),
                payload: parseError ? null : mode === "page" ? {values: parsed.value} : {document: parsed.value},
                parseError,
                changed: [],
            };
        }
        const changed = changedKeys({rows, draft: values, mode});
        return {dirty: changed.length > 0, payload: {values: diffOverride({rows, draft: values, mode})}, parseError: null, changed};
    }, [jsonActive, mode, rows, savedJsonDocument, text, values, view]);
    const dirty = canEdit && draftInfo.dirty;

    const pageExists = rows.some((row) => row.status === "page" || row.status === "extra");
    const showPageTable = mode === "page" && (pageExists || pageStarted || draftInfo.dirty);
    const clientErrors = mode === "page" && !jsonActive && showPageTable ? pageDraftErrors({rows, draft: values}) : {};
    const hasClientErrors = Object.keys(clientErrors).length > 0;

    // Debounced server preview: the «Итог» column and the warnings come from the same
    // validation and merge code the save uses.
    const payloadKey = draftInfo.payload ? stableStringify(draftInfo.payload) : "";
    const debouncedKey = useDebouncedValue(payloadKey, PREVIEW_DEBOUNCE_MS);
    const previewWanted = dirty && Boolean(payloadKey) && !hasClientErrors;
    const previewEnabled = previewWanted && debouncedKey === payloadKey;
    const previewQuery = useQuery({
        queryKey: appTextsKeys.preview(countryId, locale, section, debouncedKey),
        queryFn: ({queryKey}) => previewAppTextSection({countryId, locale, section, ...JSON.parse(queryKey[5])}),
        enabled: previewEnabled,
        staleTime: Infinity,
        gcTime: 60_000,
        retry: false,
        placeholderData: (previous) => previous,
    });
    const previewSettled = previewEnabled
        && !previewQuery.isFetching
        && !previewQuery.isPlaceholderData
        && (previewQuery.isSuccess || previewQuery.isError);
    const previewPending = previewWanted && !previewSettled;
    const previewFailed = previewSettled && previewQuery.isError;
    const previewStatus = previewQuery.error?.response?.status;
    // Only a rejected draft blocks saving; a network error or 5xx of the preview does not
    // (the save runs the same validation on the server anyway).
    const previewRejected = previewFailed && PREVIEW_REJECTED_STATUSES.has(previewStatus);
    const previewError = previewFailed ? mapApiError(previewQuery.error) : null;
    const previewData = previewWanted && !previewQuery.isError && previewQuery.data ? previewQuery.data : null;
    const displayView = previewData || view;
    const effectiveNote = previewPending ? t("sections.content.p66") : previewFailed ? t("sections.content.p67") : null;

    const fieldErrors = saveFieldErrors || (previewRejected ? previewError?.fieldErrors : null) || {};
    const errorsByKey = mergeErrors(rowFieldErrors(fieldErrors), clientErrors);
    const documentErrors = otherFieldErrors(fieldErrors);

    const tableRows = mode === "page" ? pageRows(rows, values) : rows;
    const visibleRows = tableRows.filter((row) => matchesSearch(row, values, search));
    const effectiveByKey = Object.fromEntries((displayView?.rows || []).map((row) => [row.key, row.effective]));
    const warnings = Array.isArray(displayView?.warnings) ? displayView.warnings : [];
    const groupedWarnings = warningsByKey(warnings);
    const knownKeys = new Set(tableRows.map((row) => row.key));
    const generalWarnings = jsonActive ? warnings : [
        ...groupedWarnings.general,
        ...Object.entries(groupedWarnings.byKey).filter(([key]) => !knownKeys.has(key)).flatMap(([, list]) => list),
    ];

    const saveMutation = useMutation({
        mutationFn: ({body, expectedVersion}) => saveAppTextSection({countryId, locale, section, ...body, expectedVersion}),
        onMutate: () => {
            setResult(null);
            setSaveFieldErrors(null);
        },
        onSuccess: (response) => {
            queryClient.setQueryData(sectionKey, response);
            void queryClient.invalidateQueries({queryKey: appTextsKeys.sections(countryId, locale)});
            queryClient.removeQueries({queryKey: appTextsKeys.sectionPreviews(countryId, locale, section)});
            clearStoredDraft(storageKey);
            setStoredDraft(null);
            setDraftState(null);
            setConflictKeys([]);
            setPageStarted(false);
            setResetOpen(false);
            setResult({kind: "success", message: saveResultMessage(response)});
        },
        onError: (error) => {
            setResetOpen(false);
            const status = error?.response?.status;
            if (status === 409) {
                setResult({kind: "conflict", message: CONFLICT_MESSAGE});
                return;
            }
            if (status === 403) {
                setResult({kind: "error", message: t("sections.content.p63")});
                return;
            }
            const mapped = mapApiError(error);
            setSaveFieldErrors(Object.keys(mapped.fieldErrors).length ? mapped.fieldErrors : null);
            setResult({kind: "error", message: mapped.message});
        },
    });
    const saving = saveMutation.isPending;

    const freshDraft = useCallback(() => startDraft(savedBase), [savedBase]);
    const touch = useCallback(() => {
        setSaveFieldErrors(null);
        setResult((current) => (TRANSIENT_RESULTS.has(current?.kind) ? null : current));
    }, []);
    const updateValue = useCallback((key, value) => {
        touch();
        setDraftState((current) => {
            const draft = current || freshDraft();
            return {...draft, values: {...draft.values, [key]: value}};
        });
    }, [freshDraft, touch]);
    const updateText = (next) => {
        touch();
        setDraftState((current) => ({...(current || freshDraft()), text: next}));
    };

    const save = () => {
        if (!canEdit || !draftInfo.payload) return;
        // The version the draft started from, not the latest one fetched: anything saved
        // on the server since then must answer 409 instead of being overwritten.
        saveMutation.mutate({body: draftInfo.payload, expectedVersion: draftState ? draftState.baseVersion : (view?.version ?? null)});
    };
    const closeReset = useCallback(() => setResetOpen(false), []);
    const resetSection = () => saveMutation.mutate({
        body: mode === "json" ? {document: {}} : {values: {}},
        expectedVersion: view?.version ?? null,
    });
    const discard = () => {
        setDraftState(null);
        setConflictKeys([]);
        setPageStarted(false);
        setPageView("table");
        setSaveFieldErrors(null);
        setResult(null);
        clearStoredDraft(storageKey);
        setStoredDraft(null);
    };
    const reloadLatest = async () => {
        setReloading(true);
        const response = await sectionQuery.refetch();
        setReloading(false);
        if (response.isError || !response.data) {
            setResult({
                kind: "conflict",
                message: t("sections.content.p68", {v0: appTextsErrorMessage(response.error)}),
            });
            return;
        }
        const rebased = rebaseDraft({draft: draftRef.current, ...savedDraftBase(response.data)});
        setDraftState(rebased.draft);
        setConflictKeys(rebased.conflicts);
        setSaveFieldErrors(null);
        setResult(rebaseResult(rebased));
    };
    const createFromReference = () => {
        touch();
        setPageStarted(true);
        setDraftState({
            ...freshDraft(),
            values: Object.fromEntries(rows.map((row) => [row.key, typeof row.base === "string" ? row.base : ""])),
        });
    };
    const showPageJson = () => {
        setDraftState((current) => (current
            ? {...current, text: prettyJson(diffOverride({rows, draft: draftValues(savedValues, current), mode: "page"}))}
            : null));
        setPageView("json");
    };
    const showPageTableView = () => {
        if (draftInfo.parseError) return;
        if (draftState?.text !== undefined) {
            const parsed = parseJsonDraft(draftState.text);
            const full = pageDraftFromJson(rows, parsed.value || {});
            setDraftState((current) => {
                const {text: _text, ...draft} = current || freshDraft();
                return {...draft, values: deltaFromValues(full, savedValues)};
            });
            if (Object.keys(parsed.value || {}).length) setPageStarted(true);
        }
        setPageView("table");
    };

    // Restore offer for a draft left in this browser tab (sidebar navigation, header
    // country switch and re-login cannot be blocked with BrowserRouter).
    const storedDiffers = useMemo(() => {
        if (!storedDraft || !view) return false;
        if (typeof storedDraft.documentText === "string") {
            if (mode === "json") return isJsonDraftDirty(storedDraft.documentText, savedDocument);
            if (mode !== "page") return false;
            const parsed = parseJsonDraft(storedDraft.documentText);
            if (parsed.error || pageValuesError(parsed.value)) return true;
            return stableStringify(parsed.value) !== stableStringify(savedJsonDocument);
        }
        if (mode !== "json" && storedDraft.values && typeof storedDraft.values === "object") {
            return changedKeys({rows, draft: {...savedValues, ...storedDraft.values}, mode}).length > 0;
        }
        return false;
    }, [mode, rows, savedDocument, savedJsonDocument, savedValues, storedDraft, view]);
    const showRestore = canEdit && storedDiffers;
    const restoreDraft = () => {
        const stored = storedDraft;
        setStoredDraft(null);
        if (!stored) return;
        touch();
        if (mode === "page") {
            setPageView(typeof stored.documentText === "string" ? "json" : "table");
            setPageStarted(true);
        }
        // The stored draft may predate the saved section: put it on top of the current one.
        const rebased = rebaseDraft({draft: draftFromStored(stored, savedBase), ...savedBase});
        setDraftState(rebased.draft);
        setConflictKeys(rebased.conflicts);
        if (rebased.conflicts.length || rebased.documentChanged) setResult(rebaseResult(rebased));
    };
    const dropStoredDraft = () => {
        clearStoredDraft(storageKey);
        setStoredDraft(null);
    };

    // Throttled draft persistence; flushed on unmount so a remount does not lose the tail.
    const persistRef = useRef({key: storageKey, draft: null, timer: null});
    useEffect(() => {
        const persist = persistRef.current;
        if (!view || !dirty) {
            persist.draft = null;
            return;
        }
        persist.draft = storableDraft(draftState, {kind, jsonActive, text});
        if (persist.timer) return;
        persist.timer = window.setTimeout(() => {
            persist.timer = null;
            if (persist.draft) writeStoredDraft(persist.key, persist.draft);
        }, DRAFT_PERSIST_MS);
    }, [dirty, draftState, jsonActive, kind, text, view]);
    useEffect(() => {
        const persist = persistRef.current;
        return () => {
            if (persist.timer) window.clearTimeout(persist.timer);
            persist.timer = null;
            if (persist.draft) writeStoredDraft(persist.key, persist.draft);
        };
    }, []);

    useEffect(() => {
        if (!dirty) return undefined;
        const warnBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = "";
            return "";
        };
        window.addEventListener("beforeunload", warnBeforeUnload);
        return () => window.removeEventListener("beforeunload", warnBeforeUnload);
    }, [dirty]);

    useEffect(() => {
        onDirtyChange?.(dirty);
    }, [dirty, onDirtyChange]);
    useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

    const wait = queryWaitState(sectionQuery);
    if (wait === "loading") {
        return <EditorShell><AdminTable columns={[]} loading rows={[]} /></EditorShell>;
    }
    if (wait === "offline") {
        return (
            <EditorShell>
                <div className="content-readonly-note"><WifiOff size={16} /><span>{OFFLINE_TITLE}. {OFFLINE_COPY}</span></div>
            </EditorShell>
        );
    }
    if (!view) {
        return (
            <EditorShell>
                <div className="content-texts-state">
                    <DataState title={t("sections.content.p69")} copy={appTextsErrorMessage(sectionQuery.error)} />
                    <button className="admin-row-action" onClick={() => sectionQuery.refetch()} type="button"><RefreshCw size={14} /> {t("sections.content.p70")}</button>
                </div>
            </EditorShell>
        );
    }

    const title = view.section?.title || section;
    const updated = updatedLabel(view);
    const referenceHidden = view.reference?.values_hidden === true;
    const referenceName = !referenceHidden ? view.reference?.country_name : null;
    const baseLabel = mode === "page"
        ? (referenceName ? t("sections.content.p71", {v0: referenceName}) : t("sections.content.p72"))
        : t("sections.content.p73", {v0: String(view.locale?.code || locale).toUpperCase()});
    const canSave = dirty && !saving && !reloading && Boolean(draftInfo.payload) && !previewRejected && !hasClientErrors;
    const counter = !draftInfo.dirty ? t("sections.content.p74") : draftInfo.changed.length ? t("sections.content.p75", {v0: draftInfo.changed.length}) : t("sections.content.p33");
    const effectiveDocument = displayView?.document?.effective;
    const resultIsError = result && !TRANSIENT_RESULTS.has(result.kind);

    return (
        <section aria-label={t("sections.content.p76", {v0: title})} className="content-editor content-texts-editor">
            <div className="content-workspace-head">
                <div>
                    <h3>{title}</h3>
                    {view.section?.description ? <p>{view.section.description}</p> : null}
                    <div className="content-texts-meta">
                        <StatusBadge>{KIND_LABELS[kind] || kind}</StatusBadge>
                        <code className="content-texts-mono">{view.section?.name || section}</code>
                        {updated ? <span><History aria-hidden="true" size={13} /> {updated}</span> : null}
                    </div>
                </div>
                {mode === "page" ? (
                    <div aria-label={t("sections.content.p77")} className="content-texts-toggle" role="group">
                        <button aria-pressed={pageView === "table"} disabled={Boolean(jsonActive && draftInfo.parseError)} onClick={showPageTableView} type="button"><Table2 size={14} /> {t("sections.content.p78")}</button>
                        <button aria-pressed={pageView === "json"} onClick={showPageJson} type="button"><Braces size={14} /> JSON</button>
                    </div>
                ) : null}
            </div>

            {!canEdit && !view.base_missing ? (
                <div className="content-readonly-note"><Lock size={16} /><span>{t("sections.content.p79")}</span></div>
            ) : null}

            {showRestore ? (
                <div className="content-texts-draft-banner" role="status">
                    <span>
                        {t("sections.content.p80")}{storedDraft?.savedAt ? t("sections.content.p81", {v0: formatShortDateTime(storedDraft.savedAt)}) : ""}.
                        {(storedDraft?.baseVersion ?? null) !== (view.version ?? null) ? t("sections.content.p82") : ""}
                    </span>
                    <div className="content-row-actions">
                        <button className="admin-row-action" onClick={restoreDraft} type="button"><Undo2 size={14} /> {t("sections.content.p83")}</button>
                        <button className="admin-row-action" onClick={dropStoredDraft} type="button"><Trash2 size={14} /> {t("sections.content.p84")}</button>
                    </div>
                </div>
            ) : null}

            {view.base_missing ? (
                <DataState title={t("sections.content.p85")} copy={t("sections.content.p86")} />
            ) : (
                <>
                    {generalWarnings.length ? <WarningList warnings={generalWarnings} /> : null}
                    {previewRejected && previewError?.message ? <p className="content-texts-preview-error">{previewError.message}</p> : null}
                    {previewFailed && !previewRejected ? (
                        <div className="content-texts-preview-note" role="status">
                            <span>
                                {t("sections.content.p87")} {previewStatus === 403 ? t("sections.content.p88") : previewError?.message}
                                {" "}{t("sections.content.p89")}
                            </span>
                            <button className="admin-row-action" onClick={() => previewQuery.refetch()} type="button"><RefreshCw size={14} /> {t("sections.content.p70")}</button>
                        </div>
                    ) : null}
                    {mode === "page" && !view.reference ? (
                        <div className="content-readonly-note"><Braces size={16} /><span>{t("sections.content.p90")}</span></div>
                    ) : null}
                    {mode === "page" && referenceHidden ? (
                        <div className="content-readonly-note"><Lock size={16} /><span>{t("sections.content.p91")}</span></div>
                    ) : null}

                    {mode === "json" ? (
                        <AppTextsJsonEditor
                            baseJson={prettyJson(view.document?.base)}
                            effectiveJson={effectiveDocument ? prettyJson(effectiveDocument) : ""}
                            effectiveNote={effectiveNote}
                            fieldErrors={documentErrors}
                            onChange={updateText}
                            onInsertBase={() => updateText(prettyJson(onboardingBaseDraft(view.document?.base)))}
                            parseError={draftInfo.parseError}
                            readOnly={!canEdit}
                            value={text}
                        />
                    ) : null}

                    {mode === "page" && jsonActive ? (
                        <AppTextsJsonEditor
                            baseJson={view.document?.base ? prettyJson(view.document.base) : ""}
                            baseTitle={referenceName ? t("sections.content.p92", {v0: referenceName}) : t("sections.content.p93")}
                            effectiveJson={effectiveDocument ? prettyJson(effectiveDocument) : ""}
                            effectiveNote={effectiveNote}
                            effectiveTitle={t("sections.content.p94")}
                            fieldErrors={[...documentErrors, ...Object.entries(rowFieldErrors(fieldErrors)).map(([key, messages]) => ({path: key, messages}))]}
                            insertLabel={t("sections.content.p95")}
                            label={t("sections.content.p96")}
                            onChange={updateText}
                            onInsertBase={view.document?.base ? () => updateText(prettyJson(view.document.base)) : undefined}
                            parseError={draftInfo.parseError}
                            readOnly={!canEdit}
                            value={text}
                        />
                    ) : null}

                    {mode === "page" && !jsonActive && !showPageTable ? (
                        <div className="content-texts-state">
                            <DataState
                                title={t("sections.content.p97")}
                                copy={!view.reference
                                    ? t("sections.content.p98")
                                    : referenceName
                                        ? t("sections.content.p99", {v0: referenceName})
                                        : t("sections.content.p100")}
                            />
                            {canEdit && view.reference && rows.length ? (
                                <button className="admin-row-action" onClick={createFromReference} type="button">
                                    <FilePlus2 size={14} /> {referenceHidden ? t("sections.content.p101") : t("sections.content.p102")}
                                </button>
                            ) : null}
                        </div>
                    ) : null}

                    {mode === "flat" || (mode === "page" && !jsonActive && showPageTable) ? (
                        <AppTextsTable
                            baseLabel={baseLabel}
                            canEdit={canEdit}
                            changed={new Set(draftInfo.changed)}
                            conflicts={new Set(conflictKeys)}
                            draft={values}
                            effectiveByKey={effectiveByKey}
                            effectiveNote={effectiveNote}
                            emptyMessage={tableRows.length ? t("sections.content.p103") : t("sections.content.p104")}
                            errorsByKey={errorsByKey}
                            mode={mode}
                            onChange={updateValue}
                            pageExists={pageExists}
                            rows={visibleRows}
                            warningsByKey={groupedWarnings.byKey}
                        />
                    ) : null}
                </>
            )}

            {canEdit ? (
                <div className="content-texts-actionbar">
                    {result ? (
                        <div className={resultIsError ? "content-result content-texts-result is-error" : "content-result content-texts-result"} role={resultIsError ? "alert" : "status"}>
                            <span>{result.message}</span>
                            {result.kind === "conflict" ? (
                                <button className="admin-row-action" disabled={reloading} onClick={reloadLatest} type="button">
                                    <RefreshCw size={14} /> {reloading ? t("sections.content.p105") : t("sections.content.p106")}
                                </button>
                            ) : null}
                        </div>
                    ) : null}
                    <div className="content-texts-actionbar-row">
                        <span className="content-texts-counter">{counter}</span>
                        <div className="content-editor-actions">
                            {mode !== "page" ? (
                                <button className="admin-button content-danger-button" disabled={saving || reloading || !view.version} onClick={() => setResetOpen(true)} type="button"><RotateCcw size={15} /> {t("sections.content.p107")}</button>
                            ) : null}
                            <button className="admin-button admin-button-secondary" disabled={!draftInfo.dirty || saving} onClick={discard} type="button"><Undo2 size={15} /> {t("sections.content.p108")}</button>
                            <button className="admin-button admin-button-primary" disabled={!canSave} onClick={save} type="button"><Save size={15} /> {saving ? t("sections.content.p109") : t("sections.content.p110")}</button>
                        </div>
                    </div>
                </div>
            ) : null}

            <ConfirmDialog
                confirmLabel={t("sections.content.p111")}
                copy={t("sections.content.p112")}
                onCancel={closeReset}
                onConfirm={resetSection}
                open={resetOpen}
                pending={saving}
                title={t("sections.content.p113")}
            />
        </section>
    );
};
